/**
 * Chat Session Service
 * API client for managing chat sessions
 */

import tokenManager from '../utils/tokenManager';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.releasecadence.dev/api/v1';

/**
 * Fetch all chat sessions for the current user
 * @param {string} projectId - Optional project filter
 * @param {string} featureId - Optional feature filter
 * @param {boolean} includeInactive - Include archived sessions
 * @returns {Promise<Array>} List of chat sessions
 */
export const fetchSessions = async (projectId = null, featureId = null, includeInactive = false) => {
  const token = tokenManager.getToken();
  if (!token) throw new Error('Not authenticated');

  const params = new URLSearchParams();
  if (projectId) params.append('project_id', projectId);
  if (featureId) params.append('feature_id', featureId);
  if (includeInactive) params.append('include_inactive', 'true');

  const response = await fetch(`${API_BASE}/chat-sessions?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch sessions');
  }

  return response.json();
};

/**
 * Create a new chat session
 * @param {Object} data - Session creation data
 * @returns {Promise<Object>} Created session
 */
export const createSession = async (data) => {
  const token = tokenManager.getToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE}/chat-sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error('Failed to create session');
  }

  return response.json();
};

/**
 * Update a chat session
 * @param {string} sessionId - Session ID
 * @param {Object} data - Update data
 * @returns {Promise<Object>} Updated session
 */
export const updateSession = async (sessionId, data) => {
  const token = tokenManager.getToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE}/chat-sessions/${sessionId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error('Failed to update session');
  }

  return response.json();
};

/**
 * Delete (archive) a chat session
 * @param {string} sessionId - Session ID
 * @returns {Promise<void>}
 */
export const deleteSession = async (sessionId) => {
  const token = tokenManager.getToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE}/chat-sessions/${sessionId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to delete session');
  }
};

/**
 * Link a feature to a chat session
 * @param {string} sessionId - Session ID
 * @param {string|null} featureId - Feature ID to link, or null to unlink
 * @returns {Promise<Object>} Updated session
 */
export const linkFeature = async (sessionId, featureId) => {
  const token = tokenManager.getToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE}/chat-sessions/${sessionId}/link-feature`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ feature_id: featureId })
  });

  if (!response.ok) {
    throw new Error('Failed to link feature');
  }

  return response.json();
};

/**
 * Fetch messages for a specific chat session
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} List of messages
 */
export const fetchSessionMessages = async (sessionId) => {
  const token = tokenManager.getToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE}/chat-messages/session/${sessionId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch session messages');
  }

  return response.json();
};

const chatSessionService = {
  fetchSessions,
  createSession,
  updateSession,
  deleteSession,
  linkFeature,
  fetchSessionMessages
};

export default chatSessionService;
